import lingoApi from '@/partners/lingo/lingo-giveaway/services/lingo-api'
import { computed, ref, watch } from 'vue'
import type { GachaState } from '@/partners/lingo/lingo-giveaway/types/shared/gacha'
import { GACHA_PRIZE } from '@/partners/lingo/lingo-giveaway/types/shared/gacha'
import { useNow } from '@/composables/game/time'
import type { WinnerContact, WonPrize } from '@/partners/lingo/lingo-giveaway/types/giveaway'
import { z } from 'zod'
import { validateTonAddress } from '@/helpers/utils'
import { TxFailed, useWalletConnect } from '@/composables/game/wallet-connect'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { ApiError } from '@/partners/lingo/lingo-giveaway/services/call-api'
import { nanoid } from 'nanoid'
import { useDailyCrackTheEgg } from '@/composables/game/daily'
import { DateTime } from 'luxon'

type PendingMint = { count: number, signature: string, raffleId: string }

export const winPrize = 50000

const todayEntriesCount = ref(1)
const spinState = ref<GachaState>({
  nextSpinTimestamp: 0,
  txCredit: 0,
  [GACHA_PRIZE.MAVERICK1]: 0,
  [GACHA_PRIZE.MAVERICK2]: 0,
  [GACHA_PRIZE.MAVERICK3]: 0,
  [GACHA_PRIZE.MAVERICK4]: 0,
  [GACHA_PRIZE.MAVERICK5]: 0,
})

const lastTxTimestamp = useLocalStorage(buildDropeeStorageKey('lingo_raffle_last_tx_ts'), 0)
const endOfGiveaway = ref<number>(0)

const raffleWinners = ref<Record<number, string>>({})
const raffleId = ref<number>(1)
const contractAddress = ref('')

const pendingMint = ref<PendingMint>({
  count: 2,
  signature: '',
  raffleId: '',
})

const isGold = ref<boolean>(false)
const eggWasClicked = ref<boolean>(false)

async function sync() {
  const syncResponse = await lingoApi.getSpinState()
  spinState.value = syncResponse.state
  todayEntriesCount.value = syncResponse.todayEntriesCount
  endOfGiveaway.value = syncResponse.currentRaffle.endTimestamp
  raffleId.value = syncResponse.currentRaffle.id
  contractAddress.value = syncResponse.contractAddress
  raffleWinners.value = syncResponse.winners
}

export const useLingoGiveaway = () => {
  const { now, nowDateIso } = useNow()
  const isCracking = ref(false)
  const hasExtraHatch = ref(false)
  const { doLingoRaffleTx, rawAddress } = useWalletConnect()
  const { lastEggCrack } = useDailyCrackTheEgg()
  if (spinState.value.nextSpinTimestamp > now.value && !lastEggCrack.value) {
    lastEggCrack.value = nowDateIso.value
  }

  const countDownSeconds = computed(() => Math.max(0, spinState.value.nextSpinTimestamp - now.value))

  const giveawayCountdownSeconds = computed(() => Math.max(0, endOfGiveaway.value - now.value))

  const goldenPrizeCount = computed(() => {
    const prizes = [
      spinState.value[GACHA_PRIZE.MAVERICK1],
      spinState.value[GACHA_PRIZE.MAVERICK2],
      spinState.value[GACHA_PRIZE.MAVERICK3],
      spinState.value[GACHA_PRIZE.MAVERICK4],
      spinState.value[GACHA_PRIZE.MAVERICK5],
    ]

    return Math.min(...prizes)
  })

  const goldenPrizeCountToDisplay = computed(() => {
    if (isGold.value) {
      return goldenPrizeCount.value - 1
    }
    return goldenPrizeCount.value
  })

  async function crack() {
    isCracking.value = true
    const now = DateTime.utc().toUnixInteger()
    try {
      const txHash = spinState.value.txCredit === 0 && (now - lastTxTimestamp.value) > 180 ? await doLingoRaffleTx(contractAddress.value) : nanoid(20)
      const { state, prize, entriesMint, newTicketsCount } = await lingoApi.spin({
        txHash: txHash,
        walletAddress: rawAddress.value,
      })
      lastEggCrack.value = nowDateIso.value
      lastTxTimestamp.value = 0
      updateStateTimestamp(state.nextSpinTimestamp)
      if (state.nextSpinTimestamp === 0) {
        hasExtraHatch.value = true
      }
      updatePendingMint(entriesMint)
      todayEntriesCount.value = newTicketsCount
      return prize
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TRANSACTION_ALREADY_USED') {
        lastTxTimestamp.value = 0
      }
      if (e instanceof TxFailed) {
        return undefined
      }
      return null
    } finally {
      isCracking.value = false
    }
  }

  function updateStateTimestamp(timestamp: number) {
    spinState.value.nextSpinTimestamp = timestamp
  }

  function updatePendingMint(entriesMint: PendingMint) {
    pendingMint.value = entriesMint
  }

  function resetPendingMint() {
    pendingMint.value = {
      count: 0,
      signature: '',
      raffleId: '',
    }
  }

  function addPrize(prize: GACHA_PRIZE) {
    if (!(prize in spinState.value)) return
    spinState.value[prize]++
  }

  function setGold(value: boolean) {
    isGold.value = value
  }

  function setEggWasClicked(value: boolean) {
    eggWasClicked.value = value
  }

  function resetTodayEntriesCount() {
    todayEntriesCount.value = 0
  }

  function incrementTodayEntriesCount(count: number) {
    todayEntriesCount.value += count
  }

  function resetExtraHatch() {
    hasExtraHatch.value = false
  }

  watch(goldenPrizeCount, (goldenPrizeCount, oldValue) => {
    if (eggWasClicked.value && (goldenPrizeCount === oldValue + 1)) {
      setGold(true)
    }
  })

  return {
    lingoSpinState: spinState,
    countDownSeconds,
    crack: crack,
    isCracking,
    updateStateTimestamp,
    addPrize,
    goldenPrizeCount: goldenPrizeCountToDisplay,
    pendingMint,
    resetPendingMint,
    isGold,
    setGold,
    setEggWasClicked,
    todayEntriesCount,
    resetTodayEntriesCount,
    incrementTodayEntriesCount,
    raffleId,
    raffleWinners,
    hasExtraHatch,
    resetExtraHatch,
    giveawayCountdownSeconds,
  }
}

export async function syncLingoGiveaway() {
  await sync()
}

export async function getSubmissions() {
  let contactData: WinnerContact | null = null
  const newPrizes: number[] = []

  try {
    const prizes: number[] = await getUsersWins()
    const { submissions } = await lingoApi.getWinnerSubmissions()

    const newPrizes = prizes.filter((prize) => !submissions[prize])
    const lastContactDay = Number(Object.keys(submissions).reverse()?.[0])
    if (lastContactDay) {
      contactData = submissions[lastContactDay]
    }
    return { prizes: newPrizes, contactData }
  } catch {
    return { prizes: newPrizes, contactData }
  }
}

export async function getUsersWins() {
  const response = await lingoApi.getDropeeMyWins()
  return response.raffleIds
}

export async function getUserWinsWithState() {
  try {
    const prizes: number[] = await getUsersWins()
    const { submissions } = await lingoApi.getWinnerSubmissions()
    return prizes.map((prize): WonPrize => {
      return { raffleId: prize, claimed: !!submissions[prize] }
    })
  } catch {
    return []
  }
}

export async function submitContact(prizeDays: number[], contactData: WinnerContact) {
  const errors: WinnerContact = { walletAddress: '', country: '', email: '' }
  const dataSchema = z.object({
    walletAddress: z.string().min(10).max(200),
    country: z.string().length(2),
    raffleId: z.number().min(1),
    email: z.string().email(),
    twitter: z.string().optional(),
  })
  const isValid = validateTonAddress(contactData.walletAddress)
  if (!isValid) {
    errors.walletAddress = 'error'
  }
  try {
    for (const prizeDay of prizeDays) {
      const body = {
        walletAddress: contactData.walletAddress,
        email: contactData.email,
        country: contactData.country,
        twitter: contactData.twitter,
        raffleId: prizeDay,
      }

      dataSchema.parse(body)
      await lingoApi.submitWinner(body)
    }
    return { success: true }
  } catch (e: unknown) {
    if (e instanceof z.ZodError) {
      e.errors.forEach((error) => {
        const field = error.path[0]
        errors[field as keyof WinnerContact] = error.message
      })
    } else {
      return {}
    }
  }
  return { errors }
}
